/**
 * EKSELENS System of Proof — framework library (gated download lead magnets).
 *   Route: /system-of-proof-library
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Lock, X, ArrowRight, CheckCircle2 } from "lucide-react";
import Layout from "../components/Layout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function FrameworkLibrary() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null); // doc_id being requested, or "all"
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", organisation: "", role: "", consent: false });

  useEffect(() => {
    axios.get(`${API}/public/framework-library`).then((r) => setDocs(r.data.docs))
      .catch(() => toast.error("Could not load the library"));
  }, []);

  const open = (id) => { setActive(id); setDone(false); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please tick consent to receive the framework"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/framework-library/request`, { ...form, doc_id: active });
      setDone(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <section className="relative bg-[#0B0B0C] text-white py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <span className="inline-flex items-center px-3 py-1 text-[10.5px] font-bold tracking-[0.18em] text-white bg-[#7737F6] uppercase" data-testid="fwl-badge">System of proof</span>
            <span className="text-[11px] font-semibold tracking-[0.22em] text-zinc-400 uppercase">EKSELENS · Doctrine library</span>
          </div>
          <h1 className="ek-heading text-4xl sm:text-5xl lg:text-6xl mb-6" data-testid="fwl-title">The frameworks behind the proof</h1>
          <p className="text-zinc-300 text-base md:text-lg max-w-3xl font-light" data-testid="fwl-intro">
            The doctrine that turns reinvention from persuasion into proof — our Manifesto, the Evidence Graph Maturity
            Model, and the Model Risk Management framework. Free to download; we'll email them straight to you.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid md:grid-cols-3 gap-6" data-testid="fwl-grid">
          {docs.map((d) => (
            <div key={d.id} data-testid={`fwl-card-${d.id}`} className="border border-zinc-200 rounded-lg p-6 flex flex-col hover:border-[#7737F6]/50 transition-colors">
              <FileText size={26} className="text-[#7737F6] mb-4" />
              <h3 className="ek-heading text-xl mb-2 leading-tight">{d.title}</h3>
              <p className="text-zinc-600 text-[13.5px] flex-1">{d.blurb}</p>
              <button type="button" onClick={() => open(d.id)} data-testid={`fwl-get-${d.id}`}
                className="mt-5 inline-flex items-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-[#7737F6] font-semibold hover:gap-2.5 transition-all">
                <Lock size={13} /> Download the PDF <ArrowRight size={13} />
              </button>
            </div>
          ))}
        </div>
        <div className="max-w-6xl mx-auto px-4 md:px-8 mt-8 text-center">
          <button type="button" onClick={() => open("all")} data-testid="fwl-get-all"
            className="ek-btn ek-btn-primary text-[14px]">Get all three frameworks <ArrowRight size={14} /></button>
          <p className="text-zinc-500 mt-4 text-[13px]">Prefer to test yourself first? Take the <Link to="/system-of-proof" className="text-[#7737F6] hover:underline">Evidence Graph Maturity self-assessment</Link>.</p>
        </div>
      </section>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="fwl-modal" onClick={() => setActive(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setActive(null)} data-testid="fwl-modal-close" className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
            {!done ? (
              <>
                <h3 className="ek-heading text-xl mb-1">
                  {active === "all" ? "Get all three frameworks" : `Download: ${docs.find((d) => d.id === active)?.title || ""}`}
                </h3>
                <p className="text-zinc-500 text-[13px] mb-5">We'll email the PDF{active === "all" ? "s" : ""} to you and occasionally share relevant follow-ups.</p>
                <form onSubmit={submit} className="space-y-3" data-testid="fwl-form">
                  <input required placeholder="Full name" value={form.full_name} data-testid="fwl-name"
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full border border-zinc-300 rounded px-3 py-2 text-[14px]" />
                  <input required type="email" placeholder="Work email" value={form.email} data-testid="fwl-email"
                    onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-zinc-300 rounded px-3 py-2 text-[14px]" />
                  <div className="grid grid-cols-2 gap-3">
                    <input placeholder="Organisation" value={form.organisation} data-testid="fwl-org"
                      onChange={(e) => setForm({ ...form, organisation: e.target.value })} className="border border-zinc-300 rounded px-3 py-2 text-[14px]" />
                    <input placeholder="Role" value={form.role} data-testid="fwl-role"
                      onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-zinc-300 rounded px-3 py-2 text-[14px]" />
                  </div>
                  <label className="flex items-start gap-2 text-[12px] text-zinc-500">
                    <input type="checkbox" checked={form.consent} data-testid="fwl-consent"
                      onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" />
                    I agree to Ekselens emailing me the framework(s) and relevant follow-ups. See our <Link to="/privacy" className="text-[#7737F6] underline">privacy policy</Link>.
                  </label>
                  <button type="submit" disabled={submitting} data-testid="fwl-submit"
                    className="ek-btn ek-btn-primary w-full justify-center text-[14px] disabled:opacity-40">
                    {submitting ? "Sending…" : "Email me the framework"}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-4" data-testid="fwl-done">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#7737F6] text-white rounded-full mb-4"><CheckCircle2 size={24} /></div>
                <h3 className="ek-heading text-xl mb-2">On its way.</h3>
                <p className="text-zinc-600 text-[14px]">Check your inbox — your framework{active === "all" ? "s are" : " is"} attached. Reply any time to arrange a founder-led conversation.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
